import React from 'react'
import useSWR from 'swr'
import { Navigate, useLocation } from 'react-router-dom'

import { Invoice, InvoiceStatus } from '../../types'
import { useAuth } from '../../context/auth'
import { usePageHeader } from '../../context/pageHeader'
import InvoiceForm from '../../components/invoices/form'
import { getClients, getNextCode, getClonedInvoice, getPlanLimits } from '../../api'
import useQuery from '../../context/useQuery'
import Spinner from '../../components/ui/spinner'

const EMPTY_VALUES: Invoice = {
  code: undefined,
  clientId: '',
  items: [],
  issueDate: new Date(),
  dueDate: new Date(new Date().setDate(new Date().getDate() + 7)),
  status: InvoiceStatus.DRAFT,
  locked: false
}

type NewInvoiceLocationState = {
  discardDraft?: number
}

const NewInvoice = () => {
  const { user } = useAuth()
  const location = useLocation()
  const discardDraftKey = (location.state as NewInvoiceLocationState | null)?.discardDraft
  const { setPageHeader, clearPageHeader } = usePageHeader()

  React.useEffect(() => {
    setPageHeader({
      title: 'New Invoice',
      description: 'Create a new invoice',
      hideBackButton: Number(user?.invoiceCount || 0) === 0,
      backTo: '/dashboard',
    })
    return () => clearPageHeader()
  }, [user?.invoiceCount, setPageHeader, clearPageHeader])
  const { data: planLimits, isLoading: loadingPlanLimits } = useSWR('/api/plan-limits', getPlanLimits)
  const maxInvoices = planLimits?.maxAmountOfInvoices ?? 0
  const atLimit = typeof user?.invoiceCount === 'number' && user.invoiceCount >= maxInvoices
  const hasActiveSubscription = !!user?.hasActiveSubscription
  const freeAccess = !!planLimits?.freeAccess

  const query = useQuery()
  const cloneId = query.get('cloneId')

  const { data: clients, isLoading: isLoadingClients } = useSWR('/api/clients', getClients)
  const { data: clonedInvoice, isLoading: isLoadingCloned } = useSWR(cloneId ? `/api/invoices/clone/${cloneId}` : null, getClonedInvoice)
  const [defaultValues, setDefaultValues] = React.useState({ ...EMPTY_VALUES, businessDetails: user?.businessDetails })
  const { data: nextCode } = useSWR('/api/invoices/next-code', getNextCode, {
    dedupingInterval: 0,
  })

  React.useEffect(() => {
    const newValues = {
      items: [],
      code: nextCode?.data,
    }
    setDefaultValues(v => ({...v, ...newValues}))
  }, [nextCode, user])

  let defaultClientSelected = query.get('clientId') || ''
  if(defaultClientSelected === '' && clients && clients.length === 1) {
    defaultClientSelected = clients[0]._id
  }

  const getDefaultValues = React.useCallback(() => {
    return clonedInvoice?.data ? { ...EMPTY_VALUES, ...clonedInvoice?.data } : { ...defaultValues, clientId: defaultClientSelected }
  }, [clonedInvoice, defaultValues, defaultClientSelected])

  const invoiceDefaultValues = React.useMemo(
    () => getDefaultValues(),
    [getDefaultValues]
  )

  if (!loadingPlanLimits && atLimit && !hasActiveSubscription && !freeAccess) {
    return <Navigate to='/get-premium' replace />
  }

  if(isLoadingCloned || isLoadingClients || !defaultValues.code) {
    return <Spinner />
  }

  return (
    <>
      <InvoiceForm
        key={`${discardDraftKey ?? 'invoice-new'}-${cloneId ?? 'fresh'}`}
        defaultValues={invoiceDefaultValues}
        disableDraftRestore={!!cloneId || !!discardDraftKey}
        preferInvoicePaymentInstructions={!!cloneId}
      />
    </>
  )
}

export default NewInvoice
